// @flow

import Q from 'q';

import EventEmitter from 'events';

import modals from 'modals';

import { ConfirmationResult } from '../data-models';

import {
	workflowStatuses,
	apiStatuses,
	actions,
	events,
} from '../dictionary';
import page from 'page-common';


import type {
	IController,
	ControllerOptions,
} from '../contracts';

export default class BaseController
	extends EventEmitter
	implements IController {
	constructor (options: ControllerOptions) {
		super();

		this._options = options;

		this._model = options.dataModel;
		this._viewModel = options.viewModel;
		this._template = options.template;
		this._api = options.api;
		this.busy = options.busy;
		this._outerBusy = options.outerBusy;
		this._sendSmsImmediately = options.sendSmsImmediately;
		this.timeoutService = options.timeoutService;
		this._additionalCss = options.additionalCss;

		this._confirmationModal = this._createConfirmationModal();
		this._init();
	}

	_init () {
		this._setupSubscriptions();
	}

	_createConfirmationModal = () => {
		return modals.create({
			templateMode: 'content',
			template: {
				html: this._template(),
				data: this._viewModel,
			},
			css: this._additionalCss,
		});
	}
	_setupSubscriptions = () => {
		this._confirmationModal.on(
			events.modalClosed,
			this.dispose
		);
		this._viewModel.on(
			events.checkRequest,
			this.sendCheckRequestClick
		);
		this._viewModel.on(
			events.verifyRequest,
			this.sendVerifyRequestClick
		);
	}

	_withBusy = promise => {
		this.busy.add(promise);

		if (this._outerBusy) {
			this._outerBusy.add(promise);
		}

		return promise;
	}

	_getCheckRequestRoute = () => {
		throw new Error('Not implemented');
	}

	_getVerifyRequestRoute = () => {
		throw new Error('Not implemented');
	}

	_getCheckRequestModel = () => {
		throw new Error('Not implemented');
	}

	_getVerifyRequestModel = () => {
		throw new Error('Not implemented');
	}

	_sendCheckRequest = () => {
		throw new Error('Not implemented');
	}


	sendCheckRequestClick = () => {

		if (this._model.validationGroup({ action: actions.check }).guard()) {
			return Q();
		}

		if (this.timeoutService.isAllowedToSend()) {
			this._resetCodeValue();
			
			return Q(this._withBusy(
				this._sendCheckRequest()
					.then(this._handleCheckResponse)
			)
			.done());
		}

		return Q();
	}

	_sendVerifyRequest = () => {
		throw new Error('Not implemented');
	}

	sendVerifyRequestClick = () => {
		if (this._model.validationGroup({ action: actions.verify }).guard()) {
			return Q();
		}

		return Q(this._withBusy(
			this._sendVerifyRequest()
			.then(this._handleVerifyResponse)
		))
		.done();
	}

	_handleCheckResponse = ({ responseJSON: response }) => {
		if (!response || !response.Status || response.Status !== apiStatuses.success) {
			const message = 'An error occurred while sending SMS code';
			console.warn(message);
			throw new Error(message);
		}

		this.timeoutService.updateSendMoment(!this._viewModel.hasSmsBeenSent());

		this._viewModel.hasSmsBeenSent(true);

		this._model.fromJS && this._model.fromJS(response.Data);

		return Q();
	}

	_handleVerifyResponse = ({ responseJSON: response }) => {
		if (!response || !response.Status) {
			const message = 'An error occurred while verifying SMS code';
			console.warn(message);
			throw new Error(message);
		}

		const isSuccessful = response.Status === apiStatuses.success;
		
		if (isSuccessful) {
			this.emit(events.confirmationPassed);
		} else {
			this._viewModel.Code.setError('Неверный код');
		}
		return isSuccessful;
	}

	_confirmViaModal = async () => {
		return Q.Promise((resolve: Function) => {

			this.once(
				events.confirmationPassed,
				() => resolve(new ConfirmationResult(workflowStatuses.passed))
			);

			this._confirmationModal.once(
				events.modalClosed,
				() => resolve(new ConfirmationResult(workflowStatuses.dismissed))
			);
		});
	}

	_sendInitialCheckRequest = () => {
		if (!this._sendSmsImmediately) {
			return Q();
		}

		const isValid = this._model.validationGroup({ action: actions.check }).isValid();
		if (!isValid) {
			return Q();
		}

		return Q(this.sendCheckRequestClick());
	}

	_confirm = async () => {
		await this._sendInitialCheckRequest()
			.then(() => {
				page.modals.show(this._confirmationModal);
			})
			.done();

		return this._confirmViaModal();
	}

	_setupPhone = (phone) => {
		this._model.setOuterPhone && this._model.setOuterPhone(phone);
		return Q();
	}

	_resetToInitialParams = async (): Promise<any> => {
		this._viewModel.hasSmsBeenSent(false);
		return Q();
	}

	_resetCodeValue = (): void => {
		this._model.Code('');
		this._model.Code.isModified(false);
	}

	confirm = async (phone) => {

		try {
			return Q(this._withBusy(this._resetToInitialParams())
				.then(() => {
					return this._withBusy(this._setupPhone(phone));
				})
				.then(() => {
					return this._confirm();
				})
				.then(response => {
					page.modals.hide(this._confirmationModal);
					return Q(response);
				}));
		} catch (e) {
			return Q(new ConfirmationResult(workflowStatuses.failed));
		}
	}

	dispose = (): void => {
		this._model.dispose && this._model.dispose();
		this._viewModel.dispose && this._viewModel.dispose();
	}

	handleCheckPhoneEvent = () => {
		this.sendCheckRequestClick();
	}
}
